const FLOW_STORAGE_KEY = 'tilbudgivern_quote_flow_telemetry';
const CONTEXT_STORAGE_KEY = 'tilbudgivern_product_context';

const safeStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readJson = (key, fallback = null) => {
  try {
    const value = safeStorage()?.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    safeStorage()?.setItem(key, JSON.stringify(value));
  } catch {
    // Telemetry must never block the quote flow.
  }
};

const createSessionId = () => {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `flow-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getConnectionType = () => navigator.connection?.effectiveType || undefined;

const sendEvent = async (apiBaseUrl, payload) => {
  try {
    await fetch(`${apiBaseUrl || ''}/api/product-telemetry/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });
  } catch {
    // Measurements are best-effort and must not interrupt a customer visit.
  }
};

export const setProductContext = (context = {}) => {
  const current = readJson(CONTEXT_STORAGE_KEY, {});
  const next = {
    ...current,
    ...Object.fromEntries(Object.entries(context).filter(([, value]) => value !== undefined))
  };
  writeJson(CONTEXT_STORAGE_KEY, next);
  return next;
};

export const getProductContext = () => readJson(CONTEXT_STORAGE_KEY, {});

export const getActiveQuoteFlow = () => readJson(FLOW_STORAGE_KEY, null);

export const startQuoteFlow = ({ apiBaseUrl = '', projectId, source = 'unknown' }) => {
  const existing = getActiveQuoteFlow();
  if (existing && String(existing.projectId) === String(projectId) && !existing.completedAt) {
    return existing;
  }

  const flow = {
    sessionId: createSessionId(),
    projectId,
    startedAt: Date.now(),
    milestones: {}
  };
  writeJson(FLOW_STORAGE_KEY, flow);
  setProductContext({ projectId, viewId: 'projects', stepId: 'project' });
  void sendEvent(apiBaseUrl, {
    sessionId: flow.sessionId,
    eventType: 'flow_started',
    projectId,
    viewId: 'projects',
    stepId: 'project',
    elapsedMs: 0,
    metadata: {
      source,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      connectionType: getConnectionType()
    }
  });
  return flow;
};

export const markQuoteFlowMilestone = ({
  apiBaseUrl = '',
  eventType,
  projectId,
  stepId,
  metadata = {}
}) => {
  let flow = getActiveQuoteFlow();
  if (!flow || String(flow.projectId) !== String(projectId)) {
    flow = startQuoteFlow({ apiBaseUrl, projectId, source: 'resumed_without_start' });
  }
  if (Object.prototype.hasOwnProperty.call(flow.milestones || {}, eventType)) {
    return flow;
  }

  const elapsedMs = Math.max(0, Date.now() - flow.startedAt);
  const next = {
    ...flow,
    milestones: { ...flow.milestones, [eventType]: elapsedMs },
    completedAt: eventType === 'ready_to_send' ? Date.now() : flow.completedAt
  };
  writeJson(FLOW_STORAGE_KEY, next);
  setProductContext({ projectId, viewId: 'projects', stepId });
  void sendEvent(apiBaseUrl, {
    sessionId: next.sessionId,
    eventType,
    projectId,
    viewId: 'projects',
    stepId,
    elapsedMs,
    metadata
  });
  return next;
};

export const recordContextEvent = ({ apiBaseUrl = '', eventType, metadata = {} }) => {
  const context = getProductContext();
  const flow = getActiveQuoteFlow();
  const sessionId = flow?.sessionId || createSessionId();
  void sendEvent(apiBaseUrl, {
    sessionId,
    eventType,
    projectId: context.projectId || flow?.projectId,
    viewId: context.viewId,
    stepId: context.stepId,
    elapsedMs: flow ? Math.max(0, Date.now() - flow.startedAt) : 0,
    metadata
  });
};

export const recordStandaloneProductEvent = ({
  apiBaseUrl = '',
  eventType,
  elapsedMs = 0,
  metadata = {}
}) => {
  const context = getProductContext();
  void sendEvent(apiBaseUrl, {
    sessionId: createSessionId(),
    eventType,
    projectId: context.projectId,
    viewId: context.viewId,
    stepId: context.stepId,
    elapsedMs,
    metadata
  });
};

export const recordProjectHydration = ({
  apiBaseUrl = '',
  projectId,
  phase,
  durationMs,
  slowestResource
}) => {
  const flow = getActiveQuoteFlow();
  void sendEvent(apiBaseUrl, {
    sessionId: flow?.sessionId || createSessionId(),
    eventType: phase === 'ready' ? 'project_hydration_ready' : 'project_hydration_started',
    projectId,
    viewId: 'projects',
    stepId: 'project',
    elapsedMs: flow ? Math.max(0, Date.now() - flow.startedAt) : 0,
    metadata: phase === 'ready'
      ? { durationMs, slowestResource }
      : {}
  });
};

export const __telemetryKeys = {
  FLOW_STORAGE_KEY,
  CONTEXT_STORAGE_KEY
};
